import { useMemo } from 'react';
import {
  CONVEYANCE_TYPES,
  removePickupTime,
  setPickupTime,
  getPickupTime,
  type Location,
} from '@koala/sdk/v4';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getMappedConveyanceType, getTimeSelectOption } from '@/features/handoff/time-picker/utils';
import { genericEventHandler } from '@/analytics/events';
import { GlobalEvents } from '@/analytics/events/constants';
import { useDispatch, useSelector } from '@/redux';
import basketActions from '@/redux/basket/actions';
import { BASKET_ERROR } from '@/redux/basket/messages';
import conveyanceModeActions from '@/redux/conveyanceMode/actions';
import orderStatusActions from '@/redux/orderStatus/actions';
import { createHttpClient } from '@/services/client';
import { getOrigin } from '@/utils';
import { prepareErrorMessage } from '@/utils/global';
import { type availableDaysType, isAsapAvailable, isAsapValue } from '@/utils/conveyance';
import { getStoreTimeInfo } from '@/utils/locations';
import { ASAP_VALUE } from '@/constants/global';
import { formatDate } from '@/utils/dates';
import { DATE_FORMAT } from '@/constants/dates';
import { ERROR_MESSAGES } from '@/constants/events';
import * as ErrorReporter from '@/utils/errorReporter';

interface HandoffTimesInput {
  basketId?: string;
  /** Desired pickup day in ISO8601 format. */
  dayWanted: string;
  handoffType: CONVEYANCE_TYPES;
  locationId?: number;
  location?: Location;
}

interface NextAvailableDayInput {
  availableDays: availableDaysType[];
  handoffType: CONVEYANCE_TYPES;
  location?: Location;
}

/** The HTTP status on a wretch error, or `null` for a transport failure (no response). */
function getHttpStatus(error: unknown): number | null {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === 'number' ? status : null;
}

/** Retrieves a list of available handoff times for a basket or location. */
export async function getHandoffTimes(handoffTimeInput: HandoffTimesInput & { isAsap: boolean }) {
  const hoursType =
    handoffTimeInput.handoffType === CONVEYANCE_TYPES.DELIVERY
      ? 'delivery_hours'
      : 'operating_hours';

  const origin = getOrigin(window.location.host);
  const client = createHttpClient({ origin });

  let handoffTimesResponse: string[] | null = null;
  if (handoffTimeInput.locationId && handoffTimeInput.dayWanted) {
    try {
      handoffTimesResponse = await getPickupTime(
        {
          locationId: handoffTimeInput.locationId,
          wantedAtType: hoursType,
          dayWanted: handoffTimeInput.dayWanted,
        },
        { client },
      );
    } catch (error) {
      /**
       * TEAM-15034: a failed available-times request used to report nothing, so a picker stuck on
       * "Please select a different time." was invisible to support. Reported here, at the one
       * function both hooks below call, then rethrown: the picker's own error state
       * (`time-picker.tsx`, "Please select a different time.") reads the query's `isError`, so
       * swallowing it would turn a failure into an empty list. React Query retries are off app-wide
       * (`pages/_app.tsx`), so this is one event per failed request.
       */
      ErrorReporter.captureException(error, 'error', {
        eventName: ERROR_MESSAGES.FETCH_HANDOFF_TIMES_BY_LOCATION_ERROR,
        storeId: handoffTimeInput.locationId,
        httpStatus: getHttpStatus(error),
        dayWanted: handoffTimeInput.dayWanted,
        hoursType,
      });
      throw error;
    }
  }

  /**
   * If the user wants to pick up today and the location supports
   * ASAP, prepend it to the list of available handoff times.
   *
   * NOTE: Other places in the codebase we use isStoreCurrentlyOpen(operatingHours?: LocationOperatingHours[]) function to determine if store is open
   */
  return handoffTimesResponse && handoffTimeInput.isAsap
    ? [ASAP_VALUE, ...handoffTimesResponse]
    : handoffTimesResponse;
}

export function useHandoffTimes(input: HandoffTimesInput) {
  const { storeTimezone } = getStoreTimeInfo(input.location, input.handoffType);
  const isAsap =
    isAsapAvailable(input?.location, input.handoffType) && isAsapValue(input.dayWanted);
  const handoffTimeInput = {
    basketId: input.basketId,
    dayWanted:
      input.dayWanted === ASAP_VALUE
        ? formatDate(new Date(), DATE_FORMAT.YEAR_MONTH_DAY_DASHED)
        : input.dayWanted,
    handoffType: input.handoffType,
    locationId: input.location?.id,
  };

  const query = useQuery({
    queryKey: ['handoffTimes', handoffTimeInput],
    queryFn: () =>
      getHandoffTimes({
        ...handoffTimeInput,
        isAsap,
      }),
    refetchOnWindowFocus: false,
    enabled: Boolean(handoffTimeInput.dayWanted),
    staleTime: 3600000, // 60 minutes
  });

  const { data: handoffTimes } = query;
  const timeSlotSelectOptions = useMemo(
    () =>
      handoffTimes
        ?.map(getTimeSelectOption(storeTimezone))
        ?.filter((timeSelectOption) => timeSelectOption.label) ?? [],
    [handoffTimes, storeTimezone],
  );

  return {
    ...query,
    handoffTimeSelectOptions: timeSlotSelectOptions,
  };
}

/**
 * Finds the next available day that actually has handoff time slots.
 * Iterates through available days, calling the API for each until one with slots is found.
 * Returns the day and the first real time slot on that day.
 */
export function useNextAvailableDayWithSlots(input: NextAvailableDayInput) {
  const { storeTimezone } = getStoreTimeInfo(input.location, input.handoffType);
  const enabledDays = input.availableDays.filter((day) => !day.disabled);

  return useQuery({
    queryKey: [
      'nextAvailableDayWithSlots',
      enabledDays.map((d) => d.value),
      input.handoffType,
      input.location?.id,
    ],
    queryFn: async () => {
      for (const day of enabledDays) {
        const times = await getHandoffTimes({
          dayWanted: day.value,
          handoffType: input.handoffType,
          locationId: input.location?.id,
          isAsap: false,
        });

        const firstAvailableTime = times
          ?.map(getTimeSelectOption(storeTimezone))
          .find((slot) => slot.label)?.value;

        if (firstAvailableTime) {
          return { day, firstAvailableTime };
        }
      }
      return null;
    },
    refetchOnWindowFocus: false,
    enabled: enabledDays.length > 0 && Boolean(input.location?.id),
    staleTime: 3600000, // 60 minutes
  });
}

interface SetHandoffTimeInput {
  basketId: string;
  locationId: number;
  handoffTime: string;
}

// Not being used. It is a manual duplicate of timeslotActions.setHandoffTime
async function setHandoffTime(input: SetHandoffTimeInput) {
  const origin = getOrigin(window.location.host);
  const client = createHttpClient({ origin });

  // Same as orderStatusActions.wantedAtBasketRemove()
  if (isAsapValue(input.handoffTime)) {
    try {
      const removePickupTimeResponse = await removePickupTime(
        { basketId: input.basketId, locationId: input.locationId },
        { client },
      );
      return removePickupTimeResponse;
    } catch (error) {
      console.error('Remove Pickup Time API failed:', error);
      throw error;
    }
  }

  try {
    const setPickupTimeResponse = await setPickupTime(
      {
        basketId: input.basketId,
        locationId: input.locationId,
        wanted_at: input.handoffTime,
      },
      { client },
    );

    return setPickupTimeResponse;
  } catch (error) {
    console.error('Set Pickup Time API failed:', error);
    throw error;
  }
}

// Not being used. It is a manual duplicate of timeslotActions.setHandoffTime
export function useSetHandoffTime() {
  const { checkoutBasket } = useSelector((state) => state.app.basket);
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: setHandoffTime,
    // Trigger analytics events when the mutation is fired.
    onMutate: (input) => {
      genericEventHandler(GlobalEvents.WANTED_AT_SELECTED, {
        name: checkoutBasket?.conveyance_type?.type.toLowerCase(),
        details: input.handoffTime,
      });
      return { handoffTime: input.handoffTime };
    },
    // Surface any errors that occurred when setting the handoff time.
    onError: async (e, variables) => {
      const err = await prepareErrorMessage(BASKET_ERROR.setPickupTimeSaga, e);
      dispatch(orderStatusActions.orderStatusMessageErrorSet(err.message));
      ErrorReporter.captureException(e, 'error', {
        handoffTime: variables.handoffTime,
        eventName: ERROR_MESSAGES.SET_PICKUP_TIME_ERROR,
      });
    },
    // Dispatch actions to sync up Redux with the updated basket.
    onSuccess: (basket, variables) => {
      dispatch(orderStatusActions.orderPending());
      dispatch(basketActions.success(basket));
      dispatch(
        conveyanceModeActions.setWantedTime(
          isAsapValue(basket.wanted_at) ? ASAP_VALUE : variables.handoffTime,
          Number(basket.location.id),
          getMappedConveyanceType(basket.conveyance_type.type),
        ),
      );
    },
  });
}
